import React, { useState, useEffect, useCallback } from 'react';
import { approvalsApi, formatApiErrorDetail } from '../../lib/api';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Pagination } from '../../components/Pagination';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { ClipboardCheck, Loader2, CheckCircle, XCircle, Clock, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatDate } from '../../lib/utils';

const STATUS_COLORS = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-emerald-100 text-emerald-700',
  REJECTED: 'bg-red-100 text-red-700',
  MODIFIED: 'bg-blue-100 text-blue-700',
};

export default function ApprovalsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState({ total: 0, pages: 0 });
  const [selectedReq, setSelectedReq] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  const fetchRequests = useCallback(async () => {
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await approvalsApi.list(params);
      setRequests(data.items || []);
      setPaginationData({ total: data.total || 0, pages: data.pages || 0 });
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [page, statusFilter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const openReview = async (req) => {
    try {
      const { data } = await approvalsApi.get(req.id);
      setSelectedReq(data);
    } catch (e) {
      setSelectedReq(req);
    }
    setReviewNotes('');
    setShowReview(true);
  };

  const handleReview = async (decision) => {
    if (!selectedReq) return;
    setReviewing(true);
    try {
      await approvalsApi.review(selectedReq.id, { decision, review_notes: reviewNotes });
      toast.success(`Request ${decision.toLowerCase()}`);
      setShowReview(false);
      fetchRequests();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setReviewing(false);
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="approvals-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Approval Requests</h1>
            <p className="text-slate-600 mt-1">Review data modification requests from support agents</p>
          </div>
        </div>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-40" data-testid="approval-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
                <SelectItem value="REJECTED">Rejected</SelectItem>
                <SelectItem value="MODIFIED">Modified</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-40"><Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" /></div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardCheck className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No requests</h3>
                <p className="text-slate-500 mt-1">{statusFilter === 'PENDING' ? 'No pending approval requests' : 'No requests found'}</p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-100">
                      <TableHead>Agent</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map(req => (
                      <TableRow key={req.id}>
                        <TableCell className="font-medium">{req.requested_by_name}</TableCell>
                        <TableCell>
                          <span className="text-xs font-medium bg-slate-100 px-1.5 py-0.5 rounded">{req.entity_type}</span>
                          {req.entity_id && <span className="text-xs text-slate-400 ml-1">{req.entity_id}</span>}
                        </TableCell>
                        <TableCell><Badge className="text-[10px]">{req.action_type}</Badge></TableCell>
                        <TableCell className="max-w-[200px] truncate text-slate-600 text-sm">{req.description}</TableCell>
                        <TableCell><Badge className={cn('border', STATUS_COLORS[req.status])}>{req.status}</Badge></TableCell>
                        <TableCell className="text-slate-500 text-sm">{formatDate(req.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => openReview(req)} data-testid={`review-${req.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination page={page} pages={paginationData.pages} total={paginationData.total} onPageChange={setPage} />
              </>
            )}
          </CardContent>
        </Card>

        {/* Review Dialog */}
        <Dialog open={showReview} onOpenChange={setShowReview}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Review Modification Request</DialogTitle>
              <DialogDescription>Approve, reject, or modify this data change request</DialogDescription>
            </DialogHeader>
            {selectedReq && (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-slate-400">Requested By</p>
                    <p className="font-medium text-sm">{selectedReq.requested_by_name}</p>
                    <p className="text-xs text-slate-500">{selectedReq.requested_by_email}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-slate-400">Entity</p>
                    <p className="font-medium text-sm">{selectedReq.entity_type}</p>
                    {selectedReq.entity_id && <p className="text-xs text-slate-500">ID: {selectedReq.entity_id}</p>}
                  </div>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-400">Action: <Badge className="text-[10px]">{selectedReq.action_type}</Badge></p>
                  <p className="text-sm mt-1">{selectedReq.description}</p>
                </div>

                {selectedReq.audit_logs?.length > 0 && (
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-slate-400 mb-1">Audit Trail</p>
                    {selectedReq.audit_logs.map((log, i) => (
                      <div key={i} className="text-xs py-1 border-b last:border-0 border-slate-100">
                        <span className="font-medium">{log.action}</span>
                        <span className="text-slate-400 ml-1">by {log.user_name}</span>
                        <span className="text-slate-300 ml-1">{formatDate(log.created_at)}</span>
                        {log.details && <p className="text-slate-500 mt-0.5">{log.details}</p>}
                      </div>
                    ))}
                  </div>
                )}

                {selectedReq.status === 'PENDING' && (
                  <>
                    <div className="space-y-1">
                      <Label className="text-xs">Review Notes</Label>
                      <Textarea placeholder="Add notes about your decision..." value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} rows={2} className="text-sm" data-testid="review-notes" />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => handleReview('REJECTED')} disabled={reviewing} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="reject-button">
                        {reviewing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <XCircle className="h-3 w-3 mr-1" />}Reject
                      </Button>
                      <Button size="sm" onClick={() => handleReview('APPROVED')} disabled={reviewing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-button">
                        {reviewing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}Approve
                      </Button>
                    </div>
                  </>
                )}

                {selectedReq.status !== 'PENDING' && (
                  <div className={cn('p-3 rounded-lg border', selectedReq.status === 'APPROVED' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200')}>
                    <p className="text-xs font-medium">{selectedReq.status} by {selectedReq.reviewed_by_name}</p>
                    {selectedReq.review_notes && <p className="text-xs mt-1 text-slate-600">{selectedReq.review_notes}</p>}
                    <p className="text-[10px] text-slate-400 mt-1">{formatDate(selectedReq.reviewed_at)}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
